// Threat Insights drawer — pure data builders (no React).

const OWASP_TAG_URLS = {
  "OWASP-A01-Broken-Access-Control": "https://owasp.org/Top10/A01_2021-Broken_Access_Control/",
  "OWASP-A01": "https://owasp.org/Top10/A01_2021-Broken_Access_Control/",
  "OWASP-A02-Cryptographic-Failures": "https://owasp.org/Top10/A02_2021-Cryptographic_Failures/",
  "OWASP-A02": "https://owasp.org/Top10/A02_2021-Cryptographic_Failures/",
  "OWASP-A03-Injection": "https://owasp.org/Top10/A03_2021-Injection/",
  "OWASP-A03": "https://owasp.org/Top10/A03_2021-Injection/",
  "OWASP-A04-Insecure-Design": "https://owasp.org/Top10/A04_2021-Insecure_Design/",
  "OWASP-A04": "https://owasp.org/Top10/A04_2021-Insecure_Design/",
  "OWASP-A05-Security-Misconfiguration": "https://owasp.org/Top10/A05_2021-Security_Misconfiguration/",
  "OWASP-A05": "https://owasp.org/Top10/A05_2021-Security_Misconfiguration/",
  "OWASP-A06-Vulnerable-and-Outdated-Components": "https://owasp.org/Top10/A06_2021-Vulnerable_and_Outdated_Components/",
  "OWASP-A06": "https://owasp.org/Top10/A06_2021-Vulnerable_and_Outdated_Components/",
  "OWASP-A07-Identification-and-Authentication-Failures": "https://owasp.org/Top10/A07_2021-Identification_and_Authentication_Failures/",
  "OWASP-A07": "https://owasp.org/Top10/A07_2021-Identification_and_Authentication_Failures/",
  "OWASP-A08-Software-and-Data-Integrity-Failures": "https://owasp.org/Top10/A08_2021-Software_and_Data_Integrity_Failures/",
  "OWASP-A08": "https://owasp.org/Top10/A08_2021-Software_and_Data_Integrity_Failures/",
  "OWASP-A09-Security-Logging-and-Monitoring-Failures": "https://owasp.org/Top10/A09_2021-Security_Logging_and_Monitoring_Failures/",
  "OWASP-A09": "https://owasp.org/Top10/A09_2021-Security_Logging_and_Monitoring_Failures/",
  "OWASP-A10-Server-Side-Request-Forgery": "https://owasp.org/Top10/A10_2021-Server-Side_Request_Forgery/",
  "OWASP-A10": "https://owasp.org/Top10/A10_2021-Server-Side_Request_Forgery/",
};

const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;
const CWE_REGEX = /\bCWE-(\d+)\b/gi;
const MITRE_REGEX = /\b(?:MITRE-)?(T\d{4}(?:\.\d{3})?)\b/gi;
const OWASP_REGEX = /\b(OWASP-A\d{2}(?:-[A-Za-z0-9-]+)?)\b/gi;

function addRef(refs, seen, { label, href }) {
  if (!href || seen.has(href)) return;
  seen.add(href);
  refs.push({ label: label || href, href });
}

function extractUrlsFromText(text, refs, seen) {
  if (!text) return;
  const matches = text.match(URL_REGEX) || [];
  matches.forEach((url) => {
    const cleaned = url.replace(/[.,;]+$/, "");
    addRef(refs, seen, { label: cleaned, href: cleaned });
  });
}

function extractCweRefs(text, refs, seen) {
  if (!text) return;
  let match;
  const re = new RegExp(CWE_REGEX.source, CWE_REGEX.flags);
  while ((match = re.exec(text)) !== null) {
    const id = match[1];
    const label = `CWE-${id}`;
    addRef(refs, seen, {
      label,
      href: `https://cwe.mitre.org/data/definitions/${id}.html`,
    });
  }
}

function extractMitreRefs(text, refs, seen) {
  if (!text) return;
  let match;
  const re = new RegExp(MITRE_REGEX.source, MITRE_REGEX.flags);
  while ((match = re.exec(text)) !== null) {
    const technique = match[1].toUpperCase();
    const hrefId = technique.replace(".", "/");
    addRef(refs, seen, {
      label: technique,
      href: `https://attack.mitre.org/techniques/${hrefId}/`,
    });
  }
}

function extractOwaspRefs(text, refs, seen) {
  if (!text) return;
  let match;
  const re = new RegExp(OWASP_REGEX.source, OWASP_REGEX.flags);
  while ((match = re.exec(text)) !== null) {
    const token = match[1];
    const href = OWASP_TAG_URLS[token] || OWASP_TAG_URLS[token.split("-").slice(0, 2).join("-")];
    if (href) {
      addRef(refs, seen, { label: token, href });
    }
  }
}

/**
 * Build deduped reference links from rule fields available on the tenant API.
 */
export function buildThreatInsightReferences(rule = {}) {
  const refs = [];
  const seen = new Set();
  const texts = [rule.description, rule.remediation].filter(Boolean);

  if (rule.references_url) {
    addRef(refs, seen, { label: "Primary reference", href: rule.references_url });
  }

  texts.forEach((text) => {
    extractUrlsFromText(text, refs, seen);
    extractCweRefs(text, refs, seen);
    extractMitreRefs(text, refs, seen);
    extractOwaspRefs(text, refs, seen);
  });

  return refs;
}

export function getEvidenceTruncationMessage(evidence = {}) {
  if (!evidence._truncated) return null;
  const bytes = evidence._original_bytes;
  if (bytes) {
    return `Evidence was truncated server-side (${Math.round(bytes / 1024)} KB original); samples may be incomplete.`;
  }
  return "Evidence was truncated server-side; samples may be incomplete.";
}

export function getEvidenceSampleSubtitle(matchCount, sampleCount) {
  if (!matchCount || matchCount <= sampleCount) return null;
  return `Showing ${sampleCount} representative sample${sampleCount === 1 ? "" : "s"} of ${matchCount.toLocaleString()} matches.`;
}

export function buildWhyFiredRuleFields(rule = {}, finding = {}) {
  const ruleId = finding.rule_id || rule.id;
  const fields = [];

  if (ruleId) {
    fields.push({
      key: "rule_id",
      label: "Rule ID",
      value: ruleId,
      monospace: true,
      copyable: true,
    });
  }
  if (rule.name && rule.name !== ruleId) {
    fields.push({ key: "rule_name", label: "Rule name", value: rule.name });
  }
  if (rule.category) {
    fields.push({ key: "category", label: "Category", value: rule.category });
  }
  const severity = finding.severity || rule.severity;
  if (severity) {
    fields.push({ key: "severity", label: "Severity", value: severity });
  }

  return fields;
}

export function buildWhyFiredMatchFields(finding = {}, evidence = {}, ips = [], samples = [], fmtTs) {
  const matchCount = Number(evidence.match_count || 0);
  const fields = [
    {
      key: "match_count",
      label: "Total matches",
      value: matchCount ? matchCount.toLocaleString() : "0",
    },
    {
      key: "unique_ips",
      label: "Unique source IPs",
      value: ips.length ? String(ips.length) : "0",
    },
    {
      key: "sample_count",
      label: "Samples stored",
      value: String(samples.length),
    },
  ];

  if (finding.first_seen_at && finding.last_seen_at) {
    fields.push({
      key: "activity_window",
      label: "Activity window",
      value: `${fmtTs(finding.first_seen_at)} → ${fmtTs(finding.last_seen_at)}`,
      fullRow: true,
    });
  }

  return fields;
}

const SUBJECT_KIND_LABEL = {
  ip: "Source IP",
  identity: "Identity",
  apikey: "API key",
  session: "Session",
  host: "Host",
  global: "Subject",
};

export function buildThreatContextFields({
  finding = {},
  rule = {},
  ips = [],
  matchCount = 0,
  samples = [],
  evidence = {},
  isCrossSite = false,
  subjectValue = null,
  subjectKindHint = null,
}) {
  const fields = [];

  if (isCrossSite && subjectValue) {
    fields.push({
      key: "subject",
      label: SUBJECT_KIND_LABEL[subjectKindHint] || "Subject",
      value: subjectValue,
      monospace: true,
    });
  } else {
    fields.push({
      key: "top_ips",
      label: "Top source IPs",
      value: ips.length > 0 ? ips.slice(0, 5).join(", ") : null,
      monospace: true,
      fullRow: ips.length > 3,
    });
  }

  fields.push(
    {
      key: "matches",
      label: "Total matches",
      value: matchCount ? matchCount.toLocaleString() : "0",
    },
    {
      key: "samples",
      label: "Stored samples",
      value: String(samples.length),
    },
    {
      key: "unique_ips",
      label: "Unique IPs",
      value: ips.length ? String(ips.length) : "0",
    },
  );

  if (rule.category) {
    fields.push({ key: "category", label: "Category", value: rule.category });
  }
  if (finding.site_id) {
    fields.push({
      key: "site",
      label: "Site ID",
      value: finding.site_id,
      monospace: true,
      copyable: true,
    });
  }
  if (finding.status) {
    fields.push({
      key: "status",
      label: "Finding status",
      value: finding.status,
    });
  }
  if (evidence._truncated) {
    fields.push({
      key: "truncated",
      label: "Evidence",
      value: "Truncated (see banner)",
    });
  }

  return fields;
}

export function formatSampleSummaryLine(sample, fmtTs) {
  const parts = [
    sample?.ts ? fmtTs(sample.ts) : null,
    sample?.ip || null,
    sample?.uri || null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Sample event";
}
